// src/services/CardAdapterService.js

import { AbilityService } from "./AbilityService";

/**
 * Prepare a card for viewing:
 * - Determines and sets `cardType` to "Ritual" if the card has any ritual abilities,
 *   otherwise "Fighter".
 * - Computes what elements the card provides (based on ability.element).
 * - Builds a `displayCardType` string: full element names, then the cardType.
 *   - If the card provides any non-neutral elements, it omits "Neutral".
 *   - If it provides only neutral (or nothing), it shows "Neutral".
 * - Computes `totalBaseCost` by summing all non-multiplier ability costs, then
 *   applying any multipliers (abilities with cost starting with 'x').
 * - Calculates combat stats: base 1/1/1 for Fighter cards, plus bonuses per ability.
 *   Non-Fighter cards have null stats.
 * - Ensures `cardSize` is set (default 2.3 mm).
 * - Includes the `artName` property for custom artwork.
 *
 * @param {Object} card
 * @returns {Promise<Object>}
 */
export async function GetCardForView(card) {
  const all = await AbilityService.loadAbilities();

  // 1) Determine card type
  const hasRitual =
    Array.isArray(card.abilities) &&
    card.abilities.some((id) => {
      const a = all.find((x) => x.id === id);
      return a && a.cardType === "Ritual";
    });
  const hasAttach =
    Array.isArray(card.abilities) &&
    card.abilities.some((id) => {
      const a = all.find((x) => x.id === id);
      return !!a?.description?.match(/\[attach\]/i);
    });

  const cardType = hasRitual ? "Ritual" : hasAttach ? "Attachment" : "Fighter";

  // 2) Compute what elements the card provides, via ability.element
  const elementMap = { A: "Abyssal", R: "Arcane", L: "Light", E: "Terra", X: "Neutral" };
  const rawProvides = Array.isArray(card.abilities)
    ? card.abilities
        .map((id) => {
          const a = all.find((x) => x.id === id);
          return a && a.element ? a.element : null;
        })
        .filter((sym) => !!sym)
    : [];
  const uniqueProvides = [...new Set(rawProvides)];
  const finalProvides = uniqueProvides.some((sym) => sym !== "X")
    ? uniqueProvides.filter((sym) => sym !== "X")
    : ["X"];
  const providesElements = finalProvides.map((sym) => elementMap[sym] || sym);

  // 3) Detect pure-Abyssal for Ethereal
  const isEthereal = finalProvides.length === 1 && finalProvides[0] === "A";

  // 4) Build displayCardType, prefixing "Ethereal" if needed
  const displayParts = [];
  if (isEthereal) displayParts.push("Ethereal");
  displayParts.push(...providesElements, cardType);
  const displayCardType = displayParts.join(" ");

  // 5) Compute totalBaseCost with multipliers
  let baseCost = 0;
  let multiplier = 1;
  if (Array.isArray(card.abilities)) {
    for (const id of card.abilities) {
      const a = all.find((x) => x.id === id);
      if (!a || typeof a.cost !== "string") continue;
      const costStr = a.cost.trim();
      if (/^[xX]/.test(costStr) && costStr.length > 1) {
        const m = parseFloat(costStr.substring(1));
        if (!isNaN(m)) multiplier *= m;
      } else if (costStr === "X") {
        // symbolic cost—ignored here
      } else {
        const c = parseFloat(costStr);
        if (!isNaN(c)) baseCost += c;
      }
    }
  }
  const totalBaseCost = baseCost * multiplier;

  // 6) Calculate combat stats
  let attack = null, health = null, speed = null;
  if (cardType === "Fighter") {
    attack = 1; health = 1; speed = 1;
    if (Array.isArray(card.abilities)) {
      for (const id of card.abilities) {
        const a = all.find((x) => x.id === id);
        if (a) {
          attack += a.bonusAttack || 0;
          health += a.bonusDefense || 0;
          speed += a.bonusSpeed || 0;
        }
      }
    }
  }

  // 7) Return extended card
  return {
    ...card,
    cardType,
    displayCardType,
    isEthereal,
    totalBaseCost,
    cardSize: card.cardSize ?? 2.3,
    attack,
    health,
    speed,
    artName: card.artName ?? "",
  };
}

/**
 * Prepare a card for saving:
 * - Strips everything except name, count, abilities, hardColor, cardSize, and artName.
 *
 * @param {Object} card
 * @returns {Object}
 */
export function GetCardForSave(card) {
  return {
    name: card.name,
    count: card.count,
    abilities: Array.isArray(card.abilities) ? [...card.abilities] : [],
    hardColor: !!card.hardColor,
    cardSize: card.cardSize ?? 2.3,
    artName: card.artName ?? "",
  };
}

/** Escape raw text for HTML insertion */
function escapeHtml(s = "") {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Inject symbol <img> tags into text */
function injectSymbols(text = "") {
  let t = text.replace(/\n/g, "<br>");
  const elMap = { A: "Abyssal", R: "Arcane", L: "Light", E: "Terra", X: "Neutral" };
  return t.replace(/\[([^\]]+)\]/g, (_, token) => {
    if (/^-?\d+$/.test(token)) {
      return `<img src="/symbol/Symbol${token}.png" alt="${token}" class="rules-cost-image" />`;
    }
    if (elMap[token]) {
      return `<img src="/symbol/Symbol${elMap[token]}.png" alt="${token}" class="rules-cost-image" />`;
    }
    return `<strong>${escapeHtml(token)}</strong>`;
  });
}

/**
 * Build the card body HTML by concatenating each ability’s description,
 * injecting symbols, and line-breaking between abilities.
 *
 * If a card has multiple copies of the same ability, only render its text once.
 * Replaces [c] with the count in brackets and [C] with the count number.
 * For keyword tags (e.g. [quake]), replaces with the keyword name (plain text).
 * Implements special parsing for [tf], [t], [tp] with overrides.
 *
 * @param {Object} card
 * @returns {Promise<string>}
 */
export async function GetCardBodyHtml(card) {
  const all = await AbilityService.loadAbilities();
  const keywords = await AbilityService.loadKeywords();
  const parts = [];

  if (!Array.isArray(card.abilities) || card.abilities.length === 0) {
    return "";
  }

  // Build a count map: abilityId -> number of occurrences
  const counts = card.abilities.reduce((map, id) => {
    map[id] = (map[id] || 0) + 1;
    return map;
  }, {});

  // Flags for special abilities
  const hasR93 = !!counts["r93cw7d7"];    // "all" ability
  const hasCq = !!counts["cqgv7x36"];     // multi-target ability

  // Unique abilities in first-seen order
  const uniqueIds = Array.from(new Set(card.abilities));

  // Global counters for fallback
  let globalTfCount = 0;
  let globalTCount = 0;
  let globalTpCount = 0;

  for (const id of uniqueIds) {
    // Skip the special flags themselves
    if (id === "cqgv7x36" || id === "r93cw7d7") continue;

    const ab = all.find((a) => a.id === id);
    if (!ab || !ab.description) continue;

    // Start with raw description, replace count placeholders
    let desc = ab.description
      .replace(/\[c\]/g, `[${counts[id]}]`)
      .replace(/\[C\]/g, `${counts[id]}`);

    // Keyword tags [quake], [armor], etc.
    desc = desc.replace(/\[([^\]]+)\]/g, (match, token) => {
      const kw = keywords.find(
        (k) => k.tag.toLowerCase() === token.toLowerCase()
      );
      return kw ? kw.name : match;
    });

    // Handle [tf] tags
    let localTfCount = 0;
    desc = desc.replace(/\[tf\]/gi, () => {
      if (hasR93) {
        return "all fighters";
      }
      if (hasCq) {
        localTfCount++;
        return localTfCount === 1 ? "target fighter" : "that fighter";
      }
      globalTfCount++;
      return globalTfCount === 1 ? "target fighter" : "that fighter";
    });

    // Handle [t] tags
    let localTCount = 0;
    desc = desc.replace(/\[t\]/gi, () => {
      if (hasR93) {
        return "all cards";
      }
      if (hasCq) {
        localTCount++;
        return localTCount === 1 ? "target card" : "that card";
      }
      globalTCount++;
      return globalTCount === 1 ? "target card" : "that card";
    });

    // Handle [tp] tags
    let localTpCount = 0;
    desc = desc.replace(/\[tp\]/gi, () => {
      if (hasR93) {
        return "all players";
      }
      if (hasCq) {
        localTpCount++;
        return localTpCount === 1 ? "target player" : "that player";
      }
      globalTpCount++;
      return globalTpCount === 1 ? "target player" : "that player";
    });

    parts.push(injectSymbols(escapeHtml(desc)));
  }

  return parts.join("<br>");
}

/**
 * Sort symbols in the order A, R, L, E, numeric ascending, then X
 */
function sortSymbols(arr) {
  const order = { A: 1, R: 2, L: 3, E: 4 };
  return arr.slice().sort((a, b) => {
    const ax = a === "X", bx = b === "X";
    const an = typeof a === "number" ? a : null;
    const bn = typeof b === "number" ? b : null;
    if (ax !== bx) return ax ? 1 : -1;
    if (an !== null && bn !== null) return an - bn;
    if (an !== null) return 1;
    if (bn !== null) return -1;
    return (order[a] || 99) - (order[b] || 99);
  });
}

/**
 * Compute the array of cost symbols for a card:
 * - Builds base color symbols
 * - Applies hardColor and running cost
 * - Appends X symbols
 * - Enforces 10-slot packing only if overflow
 *
 * @param {Object} card
 * @returns {Promise<Array<string|number>>}
 */
export async function GetCardSymbols(card) {
  const all = await AbilityService.loadAbilities();

  // 1) collect base colors & count X
  let base = [];
  let xCount = 0;
  for (const id of card.abilities || []) {
    const ab = all.find((a) => a.id === id);
    if (!ab) continue;
    if (ab.element && ab.element !== "X") base.push(ab.element);
    (ab.cost || "").match(/X/g)?.forEach(() => xCount++);
  }

  // 2) effective hardColor & runningCost
  const hasColors = base.length > 0;
  const hard = card.hardColor && hasColors;
  let runningCost = hard
    ? card.totalBaseCost * 0.75
    : card.totalBaseCost;

  runningCost -= base.length;

  // 3) build unbounded list
  let unbounded = [...base];
  if (!hard) {
    const n = Math.ceil(runningCost);
    if (n > 0) unbounded.push(n);
  } else {
    let idx = 0;
    while (runningCost > 0 && base.length) {
      unbounded.push(base[idx % base.length]);
      runningCost -= 1;
      idx++;
    }
  }

  // append uppercase X symbols
  for (let i = 0; i < xCount; i++) unbounded.push("X");

  if (unbounded.length <= 10) {
    return sortSymbols(unbounded);
  }

  // 4) overflow handling
  let initialNeutral = 0;
  unbounded = unbounded.filter((s) => {
    if (typeof s === "number") {
      initialNeutral += s;
      return false;
    }
    return true;
  });

  const identity = GetCardColorIdentity(unbounded);
  const final = [];
  let runningNeutral = initialNeutral;

  // a) one of each color
  identity.forEach((c) => {
    final.push(c);
    const i = unbounded.indexOf(c);
    if (i >= 0) unbounded.splice(i, 1);
  });

  // b) Xs
  const xs = unbounded.filter((s) => s === "X");
  xs.forEach(() => {
    if (final.length < 9) final.push("X");
    else runningNeutral++;
  });
  unbounded = unbounded.filter((s) => s !== "X");

  // c) fill colors left
  const colorsLeft = unbounded.filter((s) => /^[ARLE]$/.test(s));
  let ci = 0;
  while (ci < colorsLeft.length) {
    if (final.length < 9) final.push(colorsLeft[ci]);
    else runningNeutral++;
    ci++;
  }

  // d) at least one neutral
  if (runningNeutral < 1) runningNeutral = 1;
  final.push(runningNeutral);

  return sortSymbols(final);
}

/**
 * Determine a card's color identity:
 * - Unique non-neutral symbols in order, or ['X'] if none.
 *
 * @param {Array<string|number>} symbols
 * @returns {Array<string>}
 */
export function GetCardColorIdentity(symbols) {
  const id = [];
  symbols.forEach((s) => {
    if (typeof s === "string" && /^[ARLE]$/.test(s) && !id.includes(s)) {
      id.push(s);
    }
  });
  if (id.length === 0) id.push("X");
  return id;
}
